import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Calendar, HandHeart, ArrowRight, CheckCircle, Music, MapPin, Sparkles, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = `${window.location.origin}/api`;
const BENEVOLE_IMAGE = "https://customer-assets.emergentagent.com/job_brious-o-folies/artifacts/h30fgyiu_image.png";

export default function Benevoles() {
  const { t, i18n } = useTranslation();
  const widgetRef = useRef(null);

  const missions = [
    { icon: Users, title: t('benevoles.publicWelcome'), description: t('benevoles.publicWelcomeDesc') },
    { icon: MapPin, title: t('benevoles.logistics'), description: t('benevoles.logisticsDesc') },
    { icon: Music, title: t('benevoles.animation'), description: t('benevoles.animationDesc') },
    { icon: HandHeart, title: t('benevoles.helpOut'), description: t('benevoles.helpOutDesc') }
  ];

  const avantages = [
    t('benevoles.benefit1'), t('benevoles.benefit2'), t('benevoles.benefit3'),
    t('benevoles.benefit4'), t('benevoles.benefit5'), t('benevoles.benefit6')
  ];

  const dayNames = t('benevoles.dayNames', { returnObjects: true });

  useEffect(() => {
    // Remove and reload Billetweb script to force re-init on SPA navigation
    const existingScript = document.querySelector('script[src="https://www.billetweb.fr/js/export.js"]');
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.src = 'https://www.billetweb.fr/js/export.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (widgetRef.current) {
        const iframes = widgetRef.current.querySelectorAll('iframe');
        iframes.forEach(iframe => iframe.remove());
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="benevoles-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-lime-500 via-lime-600 to-green-700 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-lime-300 rounded-full blur-3xl opacity-20" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium uppercase tracking-widest mb-6">
                <Heart className="h-4 w-4" />
                Rejoignez l'aventure
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t('benevoles.title')}
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                {t('benevoles.heroDesc')}
              </p>
              <div className="mt-4 bg-white rounded-xl overflow-hidden shadow-lg" data-testid="inscription-benevole-widget">
                <a
                  title="Vente de billets en ligne"
                  href="https://www.billetweb.fr/multi_event.php?multi=44778"
                  className="shop_frame"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-src="https://www.billetweb.fr/multi_event.php?multi=44778"
                  data-max-width="100%"
                  data-initial-height="600"
                  data-scrolling="no"
                  data-id="44778"
                  data-resize="1"
                >
                  Vente de billets en ligne
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={BENEVOLE_IMAGE}
                alt="Bénévoles Brious'O Folies"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 -right-4 bg-fuchsia-600 text-white rounded-xl p-4 shadow-xl">
                <p className="font-bold text-2xl">❤️</p>
                <p className="text-sm">{t('benevoles.thanks')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-8">
            {t('benevoles.callTitle')}
          </h2>
          <div className="text-lg text-zinc-600 leading-relaxed space-y-6">
            <p dangerouslySetInnerHTML={{ __html: t('benevoles.callP1') }} />
            <p dangerouslySetInnerHTML={{ __html: t('benevoles.callP2') }} />
            <p dangerouslySetInnerHTML={{ __html: t('benevoles.callP3') }} />
          </div>
        </div>
      </section>

      {/* Widget Billetweb - Réservation */}
      <section className="py-20 bg-zinc-50" data-testid="billetweb-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('benevoles.chooseEvent')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('benevoles.eventsCalendar')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('benevoles.clickToSign')}
            </p>
          </div>
          <div
            ref={widgetRef}
            className="rounded-2xl overflow-hidden shadow-lg border border-zinc-200 bg-white"
            data-testid="benevoles-billetweb-widget"
          >
            <a
              title="Vente de billets en ligne"
              href="https://www.billetweb.fr/multi_event.php?multi=44774"
              className="shop_frame"
              target="_blank"
              rel="noopener noreferrer"
              data-src="https://www.billetweb.fr/multi_event.php?multi=44774"
              data-max-width="100%"
              data-initial-height="600"
              data-scrolling="no"
              data-id="44774"
              data-resize="1"
            >
              Vente de billets en ligne
            </a>
          </div>
        </div>
      </section>

      {/* Missions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('benevoles.findYourPlace')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('benevoles.missionTypes')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('benevoles.missionDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {missions.map((mission, index) => (
              <Card key={index} className="border-0 shadow-lg hover:-translate-y-2 transition-all duration-300 bg-white" data-testid={`mission-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-lime-500 to-green-600 flex items-center justify-center">
                    <mission.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-2">{mission.title}</h3>
                  <p className="text-sm text-zinc-600">{mission.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
                {t('benevoles.whyJoin')}
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                {t('benevoles.beingVolunteer')}
              </h2>
              <p className="text-lg text-zinc-600 mb-8 leading-relaxed">
                {t('benevoles.beingVolunteerDesc')}
              </p>
              <ul className="space-y-4">
                {avantages.map((avantage, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-lime-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-zinc-700">{avantage}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="border-0 shadow-xl bg-gradient-to-br from-lime-500 to-green-600 text-white overflow-hidden">
              <CardContent className="p-8">
                <Sparkles className="h-12 w-12 mb-6" />
                <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-4">
                  {t('benevoles.essentialDesire')}
                </h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  {t('benevoles.essentialP1')}
                </p>
                <p className="text-white/90 leading-relaxed">
                  {t('benevoles.essentialP2')}
                  <strong> {t('benevoles.essentialP2Bold')}</strong>{t('benevoles.essentialP2End')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-fuchsia-600 via-fuchsia-700 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Heart className="h-16 w-16 mx-auto mb-6 text-lime-400" />
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-6">
            {t('benevoles.ifAdventureTempts')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('benevoles.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg w-full max-w-2xl mx-auto" data-testid="cta-inscription-widget">
              <a
                title="Vente de billets en ligne"
                href="https://www.billetweb.fr/multi_event.php?multi=44778"
                className="shop_frame"
                target="_blank"
                rel="noopener noreferrer"
                data-src="https://www.billetweb.fr/multi_event.php?multi=44778"
                data-max-width="100%"
                data-initial-height="600"
                data-scrolling="no"
                data-id="44778"
                data-resize="1"
              >
                Vente de billets en ligne
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-12 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-['Playfair_Display'] text-xl font-bold mb-4">
            {t('benevoles.followSocial')}
          </h3>
          <div className="flex justify-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=61575825750948"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
              data-testid="benevoles-facebook"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            <a
              href="https://www.instagram.com/briousofolies/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full transition-colors"
              data-testid="benevoles-instagram"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
